import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MainLayout from '../../components/layout/MainLayout';
import CurrentPlanCard from './components/CurrentPlanCard';
// import ProductPlanGrid from './components/ProductPlanGrid';
import TrialPlanOffer from './components/TrialPlanOffer';
import ExtraMinutesOffer from './components/ExtraMinutesOffer';
import PlanChangeModal from './components/PlanChangeModal';
import PlanComparisonTable from './components/PlanComparisonTable';
import PaymentHistoryTable from './components/PaymentHistoryTable';
import { useProfile } from '../../hooks';
import { cancelSubscription, getPaymentHistory, getBillingPlans, createCatalogCheckoutSession, changePlan, getCandidatesCount } from '../../lib/api';
import { hasActiveSubscription, needsSubscriptionSelection } from '../../lib/utils/subscriptionAccess';
import { mergeCatalogWithStripePrices, mergeTrialWithStripePrice, mergeExtraMinutesWithStripePrices, formatCatalogNotice, shouldShowTrialOffer, getActiveBaseProductIds, planHasVoiceAI } from '../../lib/utils/billingPlans';
import { getPlanName } from '../../lib/utils/billingLabels';
import {
  getCandidatesOverLimitCount,
  getPlanMaxCandidates,
} from '../../lib/utils/planLimits';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import Button from '../../components/ui/Button';
import { format } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { createPortal } from 'react-dom';

function getCatalogMonthlyPriceEur(product) {
  const amount = product?.prices?.month?.amount;
  return amount ? amount / 100 : null;
}

function formatPeriodEnd(value) {
  if (value == null || value === '') return null;
  try {
    const date = typeof value === 'string' ? new Date(value) : new Date(value);
    return isNaN(date.getTime()) ? null : format(date, 'dd/MM/yyyy');
  } catch {
    return null;
  }
}

const Billing = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { profile, loading: profileLoading, refetch: refetchProfile } = useProfile();
  const [catalogNotice, setCatalogNotice] = useState('');
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogProducts, setCatalogProducts] = useState(() => mergeCatalogWithStripePrices());
  const [trialProduct, setTrialProduct] = useState(() => mergeTrialWithStripePrice());
  const [extraMinutesProducts, setExtraMinutesProducts] = useState(() => mergeExtraMinutesWithStripePrices());
  const [devPrice, setDevPrice] = useState(null);
  const [selectingKey, setSelectingKey] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const [catalogMessage, setCatalogMessage] = useState('');
  const [planChangeTarget, setPlanChangeTarget] = useState(null);
  const [planChanging, setPlanChanging] = useState(false);
  const [planChangeError, setPlanChangeError] = useState('');
  const [checkoutConfirm, setCheckoutConfirm] = useState(null);
  useEffect(() => {
    let cancelled = false;

    const loadCatalogPrices = async () => {
      setCatalogLoading(true);
      setCatalogNotice('');
      try {
        const { prices, warnings } = await getBillingPlans();
        if (!cancelled) {
          setCatalogProducts(mergeCatalogWithStripePrices(prices));
          setTrialProduct(mergeTrialWithStripePrice(prices));
          setExtraMinutesProducts(mergeExtraMinutesWithStripePrices(prices));
          setDevPrice(prices?.dev?.month ?? null);
          setCatalogNotice(formatCatalogNotice({ warnings }));
        }
      } catch (err) {
        if (!cancelled) {
          setCatalogProducts(mergeCatalogWithStripePrices());
          setTrialProduct(mergeTrialWithStripePrice());
          setExtraMinutesProducts(mergeExtraMinutesWithStripePrices());
          setDevPrice(null);
          setCatalogNotice(formatCatalogNotice({ errorMessage: err?.message }));
        }
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    };

    loadCatalogPrices();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (searchParams.get('success') === '1' && refetchProfile) {
      refetchProfile();
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, refetchProfile, setSearchParams]);

  const currentPlanData = useMemo(() => {
    const plan = profile?.subscription_plan?.toLowerCase() || null;
    const isTrialPlan = plan === 'trial';
    const active = hasActiveSubscription(profile);
    const noPlanSelected = needsSubscriptionSelection(profile);
    const statusValue = profile?.subscription_status;
    const isExpiredTrial = isTrialPlan && statusValue === 'expired';

    const statusKey = statusValue === 'over_limit'
      ? 'over_limit'
      : statusValue === 'past_due'
        ? 'past_due'
        : active && profile?.subscription_cancel_at_period_end
          ? 'canceling'
          : active
            ? 'active'
            : statusValue === 'expired'
              ? (isTrialPlan ? 'trial_ended' : 'expired')
              : 'select_a_plan';

    const statusLabels = {
      over_limit: t('common.overLimit'),
      past_due: t('common.pastDue'),
      canceling: t('common.canceling'),
      active: t('common.active'),
      trial_ended: t('billing.trialEnded'),
      expired: t('common.expired'),
      select_a_plan: t('common.selectAPlan'),
    };

    const typeLabel = active && plan
      ? (isTrialPlan ? t('billing.trialType') : getPlanName(plan))
      : isExpiredTrial
        ? t('billing.trialEnded')
        : t('common.noPlanSelected');

    const price = active && plan
      ? (isTrialPlan
        ? 0
        : (getCatalogMonthlyPriceEur(catalogProducts.find((product) => product.id === plan))
          ?? 0))
      : 0;

    let nextPaymentDate = t('common.na');
    const periodEnd = formatPeriodEnd(isTrialPlan
      ? (profile?.trial_ends_at || profile?.current_period_end)
      : profile?.current_period_end);
    if (periodEnd) nextPaymentDate = periodEnd;

    const cancelAtPeriodEnd = !!profile?.subscription_cancel_at_period_end;

    return {
      planId: plan,
      typeLabel,
      statusKey,
      status: statusLabels[statusKey],
      price,
      nextPaymentDate,
      noPlanSelected,
      cancelAtPeriodEnd,
      isTrial: isTrialPlan,
    };
  }, [profile, catalogProducts, t]);

  const showTrialOffer = shouldShowTrialOffer(profile);

  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(true);
  const [transactionsError, setTransactionsError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setTransactionsLoading(true);
    setTransactionsError('');
    getPaymentHistory()
      .then(({ transactions: list }) => {
        if (!cancelled) setTransactions(list ?? []);
      })
      .catch((err) => {
        if (!cancelled) setTransactionsError(err?.message || t('billing.paymentHistoryFailed'));
      })
      .finally(() => {
        if (!cancelled) setTransactionsLoading(false);
      });
    return () => { cancelled = true; };
  }, [profile?.subscription_plan, profile?.stripe_customer_id, t]);

  const scrollToPlans = () => {
    document.getElementById('plans-table')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleUpgrade = scrollToPlans;

  const startCatalogCheckout = async ({ product, interval, price }) => {
    const selectKey = `${product.id}-${interval}`;
    setSelectingKey(selectKey);
    setCatalogMessage('');

    try {
      if (!price?.priceId) {
        setCatalogMessage(t('billing.stripePriceNotConfigured'));
        return;
      }

      const { url } = await createCatalogCheckoutSession({
        productId: product.id,
        interval,
        priceId: price.priceId,
      });
      window.location.href = url;
    } catch (err) {
      setCatalogMessage(err?.message || t('billing.checkoutFailed'));
    } finally {
      setSelectingKey('');
    }
  };

  const handleSelectCatalogPlan = async ({ product, interval, price }) => {
    const targetMax = getPlanMaxCandidates(product.id);
    const currentMax = Number(profile?.max_candidates ?? 0);

    // Immediate checkout onto a lower candidate limit (e.g. trial → Essential).
    if (hasActiveSubscription(profile) && targetMax < currentMax && user?.id) {
      try {
        setSelectingKey(`${product.id}-${interval}`);
        const count = await getCandidatesCount(user.id);
        const excess = getCandidatesOverLimitCount(count, targetMax);
        if (excess > 0) {
          setCheckoutConfirm({ product, interval, price, excess, limit: targetMax });
          return;
        }
      } catch (err) {
        console.error('Failed to check candidate count before checkout:', err);
      } finally {
        setSelectingKey('');
      }
    }

    await startCatalogCheckout({ product, interval, price });
  };

  const handleStartTrial = async ({ trial, price }) => {
    setSelectingKey(trial.id);
    setCatalogMessage('');

    try {
      if (!price?.priceId) {
        setCatalogMessage(t('billing.stripePriceNotConfiguredTrial'));
        return;
      }

      const { url } = await createCatalogCheckoutSession({
        productId: trial.id,
        priceId: price.priceId,
      });
      window.location.href = url;
    } catch (err) {
      setCatalogMessage(err?.message || t('billing.checkoutFailed'));
    } finally {
      setSelectingKey('');
    }
  };

  const handleUseDevPlan = async () => {
    setSelectingKey('dev');
    setCatalogMessage('');

    try {
      if (canSwitchPlan) {
        setCatalogMessage(t('billing.devPlanHasSubscription'));
        return;
      }
      if (!devPrice?.priceId) {
        setCatalogMessage(t('billing.stripePriceNotConfigured'));
        return;
      }

      const { url } = await createCatalogCheckoutSession({
        productId: 'dev',
        interval: 'month',
        priceId: devPrice.priceId,
      });
      window.location.href = url;
    } catch (err) {
      setCatalogMessage(err?.message || t('billing.checkoutFailed'));
    } finally {
      setSelectingKey('');
    }
  };

  const handleBuyExtraMinutes = async ({ product, price }) => {
    setSelectingKey(product.id);
    setCatalogMessage('');

    try {
      if (!price?.priceId) {
        setCatalogMessage(t('billing.stripePriceNotConfiguredPack'));
        return;
      }

      const { url } = await createCatalogCheckoutSession({
        productId: product.id,
        priceId: price.priceId,
        returnPath: '/billing',
      });
      window.location.href = url;
    } catch (err) {
      setCatalogMessage(err?.message || t('billing.checkoutFailed'));
    } finally {
      setSelectingKey('');
    }
  };

  const showExtraMinutes = planHasVoiceAI(profile);

  // A real, switchable Stripe subscription (trials have no subscription id).
  const canSwitchPlan = Boolean(profile?.stripe_subscription_id) &&
    ['active', 'canceled', 'over_limit'].includes(profile?.subscription_status);

  const handleOpenPlanChange = ({ product, interval, price }) => {
    setPlanChangeError('');
    setPlanChangeTarget({ product, interval, price });
  };

  const handleConfirmPlanChange = async (target) => {
    setPlanChanging(true);
    setPlanChangeError('');
    try {
      const result = await changePlan({
        productId: target.product.id,
        priceId: target.price.priceId,
        interval: target.interval,
      });
      await refetchProfile();
      setPlanChangeTarget(null);
      const name = getPlanName(target.product.id);
      setCatalogMessage(
        result?.direction === 'downgrade'
          ? t('billing.pendingSwitchEnd', { name })
          : t('billing.upgradedTo', { name }),
      );
    } catch (err) {
      setPlanChangeError(err?.message || t('billing.changePlanFailed'));
    } finally {
      setPlanChanging(false);
    }
  };

  const pageLoading = (profileLoading && !profile) || catalogLoading;

  const handleCancelSubscription = async () => {
    setCancelError('');
    setCancelLoading(true);
    try {
      await cancelSubscription();
      await refetchProfile();
    } catch (err) {
      setCancelError(err?.message || t('billing.cancelFailed'));
    } finally {
      setCancelLoading(false);
    }
  };

  if (pageLoading) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">{t('billing.loading')}</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  const pendingPlanName = profile?.pending_plan
    ? getPlanName(profile.pending_plan.toLowerCase())
    : '';
  const pendingDate = formatPeriodEnd(profile?.pending_plan_effective_at);

  return (
    <MainLayout>
      <div className="space-y-6 md:space-y-8 py-6 md:py-8">
        <div>
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">
            {t('billing.title')}
          </h1>
          <p className="text-sm md:text-base lg:text-lg text-muted-foreground">
            {t('billing.subtitle')}
          </p>
        </div>

        {currentPlanData.noPlanSelected && currentPlanData.statusKey === 'select_a_plan' ? (
          showTrialOffer ? (
            <div className="bg-card border border-border rounded-lg p-4 md:p-6 lg:p-8 shadow-elevation-md">
              <TrialPlanOffer
                trial={trialProduct}
                loading={catalogLoading}
                selecting={selectingKey === trialProduct.id}
                onStartTrial={handleStartTrial}
              />
            </div>
          ) : null
        ) : (
          <CurrentPlanCard
            plan={currentPlanData}
            onUpgrade={handleUpgrade}
            onCancel={handleCancelSubscription}
            cancelLoading={cancelLoading}
            cancelError={cancelError}
          />
        )}

        {cancelError && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {cancelError}
          </div>
        )}

        {catalogMessage && (
          <div className="rounded-lg border border-primary/30 bg-primary/10 px-4 py-3 text-sm text-foreground">
            {catalogMessage}
          </div>
        )}

        {profile?.pending_plan && (
          <div className="rounded-lg border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-foreground">
            {pendingDate
              ? t('billing.pendingSwitch', { name: pendingPlanName, date: pendingDate })
              : t('billing.pendingSwitchEnd', { name: pendingPlanName })}
          </div>
        )}

        {/*
        <div id="smartwatcher-plans" className="space-y-4 md:space-y-6">
          <div>
            <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground mb-2">
              {t('billing.smartWatcherPlans')}
            </h2>
            <p className="text-sm md:text-base text-muted-foreground">
              {t('billing.plansIntro')}
            </p>
          </div>

          <ProductPlanGrid
            products={catalogProducts}
            loading={catalogLoading}
            onSelectPlan={handleSelectCatalogPlan}
            onChangePlan={handleOpenPlanChange}
            selectingKey={selectingKey}
            currentPlanId={getActiveBaseProductIds(profile)[0] ?? null}
            currentInterval={profile?.subscription_interval ?? null}
            canSwitch={canSwitchPlan}
          />
        </div>
        */}

        {catalogNotice && (
          <div className="rounded-lg border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-foreground">
            {catalogNotice}
          </div>
        )}

        {showTrialOffer && currentPlanData.statusKey !== 'select_a_plan' && (
          <TrialPlanOffer
            trial={trialProduct}
            loading={catalogLoading}
            selecting={selectingKey === trialProduct.id}
            onStartTrial={handleStartTrial}
          />
        )}

        {showExtraMinutes && (
          <ExtraMinutesOffer
            products={extraMinutesProducts}
            loading={catalogLoading}
            selectingId={selectingKey}
            onBuy={handleBuyExtraMinutes}
          />
        )}

        <div id="plans-table">
          <div className="mb-4 max-w-4xl md:mb-6">
            <h2 className="text-3xl font-semibold leading-tight text-foreground sm:text-4xl">
              {t('billing.availablePlans')}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              {t('billing.choosePlanFit')}
            </p>
          </div>
          <PlanComparisonTable
            products={catalogProducts}
            loading={catalogLoading}
            onSelectPlan={handleSelectCatalogPlan}
            onChangePlan={handleOpenPlanChange}
            selectingKey={selectingKey}
            currentPlanId={getActiveBaseProductIds(profile)[0] ?? null}
            currentInterval={profile?.subscription_interval ?? null}
            canSwitch={canSwitchPlan}
          />

          {profile?.dev_plan_available && (
            <div className="mt-6 flex justify-center">
              <Button
                variant="outline"
                loading={selectingKey === 'dev'}
                disabled={selectingKey === 'dev'}
                onClick={handleUseDevPlan}
              >
                {t('billing.useDevPlan')}
              </Button>
            </div>
          )}
        </div>

        {(transactions.length > 0 || currentPlanData?.cancelAtPeriodEnd) && (
          <PaymentHistoryTable
            transactions={transactions}
            loading={transactionsLoading}
            error={transactionsError}
            cancelAtPeriodEnd={currentPlanData?.cancelAtPeriodEnd}
            cancelDate={currentPlanData?.nextPaymentDate}
          />
        )}
      </div>

      <PlanChangeModal
        open={Boolean(planChangeTarget)}
        target={planChangeTarget}
        onClose={() => { if (!planChanging) setPlanChangeTarget(null); }}
        onConfirm={handleConfirmPlanChange}
        confirming={planChanging}
        error={planChangeError}
      />

      {createPortal(
        <ConfirmDialog
          isOpen={Boolean(checkoutConfirm)}
          onClose={() => setCheckoutConfirm(null)}
          onConfirm={async () => {
            const target = checkoutConfirm;
            setCheckoutConfirm(null);
            if (target) {
              await startCatalogCheckout(target);
            }
          }}
          title={t('billing.overLimitWarningTitle')}
          message={checkoutConfirm
            ? t('billing.overLimitCheckoutWarning', {
              count: checkoutConfirm.excess,
              limit: checkoutConfirm.limit,
              name: getPlanName(checkoutConfirm.product.id),
            })
            : ''}
          confirmText={t('billing.confirmChange')}
          cancelText={t('common.cancel')}
          variant="default"
        />,
        document.body
      )}
    </MainLayout>
  );
};

export default Billing;
